/**
 * PagFinance design tokens.
 *
 * Source of truth: the PagFinance design system ("The Regulated Bridge").
 * Raw values are copied verbatim; only the semantic roles are chosen here.
 *
 * This screen is a viewfinder sitting on top of a live camera feed, so it
 * adopts the dark side of the dual-theme system: navy-deep page, navy-elevated
 * surfaces, teal-bright as the single accent. Components must consume the
 * semantic tokens below, never the raw palette.
 */

export const palette = {
  navyDeep: "#0B1E3F",
  navyElevated: "#10264F",
  navyHover: "#1A3160",
  slateBody: "#4A5A73",
  slateMid: "#6B7D95",
  slateMuted: "#8596AE",
  slateLight: "#A8B5C7",
  slateBorder: "#D5DEEA",
  slatePage: "#F7F9FC",
  pureWhite: "#FFFFFF",
  tealDeep: "#00867A",
  tealAccent: "#00B8A9",
  tealBright: "#14E0CC",
  amberWarning: "#FBBF24",
};

export const token = {
  bgPage: palette.navyDeep,
  bgSurface: palette.navyElevated,
  bgHover: palette.navyHover,

  textPrimary: palette.pureWhite,
  textSecondary: palette.slateLight,
  textMuted: palette.slateMuted,
  textOnAccent: palette.navyDeep,

  // Teal Bright takes the primary accent role on navy. Teal Accent is the
  // hover step, keeping navy text in dark mode.
  accent: palette.tealBright,
  accentHover: palette.tealAccent,
  warning: palette.amberWarning,

  // The Alpha Border Rule: on navy, borders are white at low alpha. Solid
  // slate values read as harsh artificial lines.
  borderDefault: "rgba(255, 255, 255, 0.08)",
  borderStrong: "rgba(255, 255, 255, 0.16)",

  // Flat at rest. Shadows are state feedback only.
  shadowFloat: "0 8px 24px rgba(0, 0, 0, 0.32)",
  focusRing: "0 0 0 2px rgba(20, 224, 204, 0.4)",
};

export const radius = {
  sm: "4px",
  md: "6px",
  lg: "8px",
  pill: "999px",
};

export const space = {
  xs: "8px",
  sm: "12px",
  md: "16px",
  lg: "24px",
  xl: "32px",
};

export const font = {
  /**
   * Inter with a system stack fallback. No webfont is loaded on purpose: this
   * page is opened from wallet in-app browsers where a blocked or slow font
   * request would delay the viewfinder, and the system stack is metrically
   * close enough for the little text this screen carries.
   */
  sans:
    "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, " +
    "'Helvetica Neue', Arial, sans-serif",
  mono:
    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, " +
    "'Courier New', monospace",
};

/** Mechanical, deliberate. Matches the ease-pf-out curve. */
export const ease = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * Reticle colours, consumed by the canvas painter in src/scan. Canvas cannot
 * read CSS custom properties, so the accent has to be handed over as a value.
 */
export const canvasColor = {
  reticle: "rgba(20, 224, 204, 0.9)",
  mask: "rgba(11, 30, 63, 0.45)",
};
